import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpHeaders } from '@angular/common/http';
import { Observable, switchMap } from 'rxjs';
import { IFile, IUserPhoto, IUserPhotoResponse, IPhotoUploadLinkResponse, IUserPhotoCompleteRequest, IUserPhotoValidateRequest, IPhotoUploadLinkActiveResponse } from '../entities/filegroup.interface';
import { APP_CONFIG } from '@application/config';
import { PhotoFlags } from '../entities/filegroup.enum';
import { EDeviceAction } from '../filegroup.store';

@Injectable({ providedIn: 'root' })
export class FilegroupService {
  public readonly http = inject(HttpClient);
  private readonly apiUrl = inject(APP_CONFIG).apiUrl;

  public getFile(id: string): Observable<IFile> {
    return this.http.get<IFile>(
      this.apiUrl.file_group_service + '/Files' + `?id=${id}`,
      {
        withCredentials: true,
      }
    );
  }

  public createFile(payload: Partial<IFile>): Observable<HttpEvent<IFile>> {
    return this.http.post<IFile>(
      this.apiUrl.file_group_service + '/Files',
      payload,
      {
        withCredentials: true,
        observe: 'events',
        reportProgress: true,
      }
    )
  }

  public updateFile(payload: Partial<IFile>): Observable<HttpEvent<IFile>> {
    return this.http.put<IFile>(
      this.apiUrl.file_group_service + '/Files',
      payload,
      {
        withCredentials: true,
        observe: 'events',
        reportProgress: true,
      }
    );
  }

  public deleteFile(id: string): Observable<IFile> {
    return this.http.delete<IFile>(
      this.apiUrl.file_group_service + '/Files' + `?id=${id}`,
      {
        withCredentials: true,
      }
    );
  }

  public getUserPhotos(payload: string, flag?: PhotoFlags): Observable<IUserPhotoResponse[]> {
    const params = flag != null ? { groupId: payload, flag } : { groupId: payload };

    return this.http.get<IUserPhotoResponse[]>(
      this.apiUrl.file_group_service + '/UserPhotos',
      {
        params,
        withCredentials: true,
      }
    );
  }

  public updateUserPhotos(payload: Partial<IUserPhoto>): Observable<IUserPhotoResponse> {
    return this.http.post<IUserPhotoResponse>(
      this.apiUrl.file_group_service + '/UserPhotos',
      payload,
      {
        withCredentials: true,
      }
    );
  }

  public deleteUserPhotos(id: string): Observable<string> {
    return this.http.delete<string>(
      this.apiUrl.file_group_service + '/UserPhotos',
      {
        params: { id },
        withCredentials: true,
      }
    );
  }

  public createUserPhotos(payload: IUserPhoto): Observable<IUserPhotoResponse> {
    return this.http.post<IUserPhotoResponse>(
      this.apiUrl.file_group_service + '/UserPhotos',
      payload,
      {
        withCredentials: true,
      }
    );
  }

  public setFlagUserPhotos({ fileId, flag }: { fileId: string; flag: PhotoFlags }): Observable<void> {
    return this.http.put<void>(
      this.apiUrl.file_group_service +
      '/UserPhotos/SetFlagAsync?',
      null,
      {
        params: { fileId, flag },
        withCredentials: true,
      }
    );
  }

  public clearFlagUserPhotos({ fileId, flag }: { fileId: string; flag: PhotoFlags }): Observable<void> {
    return this.http.put<void>(
      this.apiUrl.file_group_service +
      '/UserPhotos/ClearFlagAsync?',
      null,
      {
        params: { fileId, flag },
        withCredentials: true,
      }
    );
  }

  public getActuarProfilePhoto(payload: string): Observable<IUserPhotoResponse[]> {
    const headers = new HttpHeaders({
      'loadingBarIgnored': 'true'
    });

    return this.http.get<IUserPhotoResponse[]>(
      this.apiUrl.file_group_service +
      '/UserPhotos/GetActuarProfile?groupId=' + payload,
      {
        withCredentials: true,
        headers,
      }
    );
  }

  public getActuarProfileAsync(payload: string): Observable<IUserPhotoResponse[]> {
    const headers = new HttpHeaders({
      'loadingBarIgnored': 'true'
    });

    return this.http.get<IUserPhotoResponse[]>(
      this.apiUrl.file_group_service +
      '/UserPhotos/GetActuarProfileAsync?groupId=' + payload,
      {
        headers,
      }
    );
  }


  public switchDevices(deviceName: string, action: EDeviceAction): Observable<any> {
    return this.http.get<any>(`${this.apiUrl.acessoLocalhost}/DeviceAction?DeviceName=${deviceName}&action=${action}`, {
        withCredentials: true
    });
  } 

  public getFilesByGroupId(groupId: string, folder: number): Observable<IFile[]> {
    return this.http.get<IFile[]>(
      this.apiUrl.file_group_service + '/Files/GetByGroupIdAsync',
      {
        params: { groupId, folder },
        withCredentials: true,
      }
    );
  }

  public createPhotoUploadLink(personId: string): Observable<IPhotoUploadLinkResponse> {
    return this.http.post<IPhotoUploadLinkResponse>(
      this.apiUrl.file_group_service + '/UserPhotoUpload/create-upload',
      { PersonId: personId },
      {
        withCredentials: true,
      }
    );
  }

  public completeUserPhoto(payload: IUserPhotoCompleteRequest): Observable<IUserPhotoResponse> {
    return this.http.post<IUserPhotoResponse>(
      this.apiUrl.file_group_service + '/UserPhotos/complete',
      payload,
      {
        withCredentials: false, // Endpoint não autenticado, token enviado no corpo
      }
    );
  }

  public validateUserPhotoUpload(payload: IUserPhotoValidateRequest): Observable<boolean> {
    return this.http.post<boolean>(
      this.apiUrl.file_group_service + '/UserPhotoUpload/validate',
      payload,
      {
        withCredentials: false, // Endpoint não autenticado, token enviado no corpo
      }
    );
  }

  public checkPhotoUploadLinkActive(personId: string): Observable<IPhotoUploadLinkActiveResponse> {
    return this.http.post<IPhotoUploadLinkActiveResponse>(
      this.apiUrl.file_group_service + '/UserPhotoUpload/active',
      { PersonId: personId },
      {
        withCredentials: true,
      }
    );
  }
}
